import { z } from 'zod';
import { normalizedUuidSchema as uuidSchema } from './common';
import {
  quoteIntegerSchema,
  quoteQuantitySchema,
  splitVatInclusiveTotal,
} from './quote-primitives';
import { destinationSizeListSchema, destinationSizeSchema } from './quote-sizes';
import { SHIPPING_ADDRESS_MAX_LENGTHS, shippingAddressSchema } from './shipping-address';

// ---------------------------------------------------------------------------
// 배송비·다중 배송지(SUPER-86, spec docs/superpowers/specs/2026-10-20-shipping-fee-multi-destination-design.md §4-2·§7).
// 지역은 저장하지 않고 우편번호에서 매번 구한다 — 금액은 발행본 줄에 고정돼 목록이 바뀌어도 과거 금액은 변하지 않는다.
// api(요청·초안·주문)·web(임시 견적)·admin(초안 편집)·worker(메일)가 이 파일 하나를 쓴다.
// ---------------------------------------------------------------------------

export const SHIPPING_REGIONS = ['STANDARD', 'JEJU', 'REMOTE'] as const;
export type ShippingRegion = (typeof SHIPPING_REGIONS)[number];
export const shippingRegionSchema = z.enum(SHIPPING_REGIONS);
export const SHIPPING_REGION_LABELS: Readonly<Record<ShippingRegion, string>> = {
  STANDARD: '일반',
  JEJU: '제주',
  REMOTE: '도서산간',
};

export const MAX_DESTINATIONS = 10;
export const SHIPPING_FEE_MAX = 1_000_000;

/** 제주특별자치도 우편번호(포함 범위). */
export const JEJU_POSTAL_CODE_RANGE: readonly [number, number] = [63000, 63644];

/**
 * 기타 도서산간(제주 제외) 우편번호 범위 [시작, 끝](포함), 오름차순·겹침 없음.
 * 택배사 표와 대조 전 임시 목록 — 택배사 공용 도서산간 우편번호 목록(2015-08 5자리 국가기초구역 우편번호 기준)을
 * 2026-10-20 정리했다(spec §11 Q2). 운영 공개 전에 계약 택배사의 도서산간 우편번호표와 대조해 검증하고 바꿔야 한다
 * (docs/operations/quotes.md 배송비 절).
 * 바꿔도 이미 발행한 견적 금액은 줄에 고정돼 변하지 않는다.
 */
export const REMOTE_POSTAL_CODE_RANGES: readonly (readonly [number, number])[] = [
  [22386, 22388], // 인천 중구 섬 지역(무의도 등)
  [23004, 23010], // 인천 강화군 섬 지역(교동·삼산·서도면)
  [23100, 23116], // 인천 옹진군 섬 지역(북도·연평면 등)
  [23124, 23136], // 인천 옹진군 섬 지역(백령·대청·덕적·자월면 등)
  [31708, 31708], // 충남 당진시 섬 지역
  [32133, 32133], // 충남 태안군 섬 지역
  [33411, 33411], // 충남 보령시 섬 지역
  [40200, 40240], // 경북 울릉군
  [46768, 46771], // 부산 강서구 섬 지역(가덕도 등)
  [52570, 52571], // 경남 사천시 섬 지역
  [53031, 53033], // 경남 통영시 섬 지역
  [53089, 53104], // 경남 통영시 섬 지역
  [54000, 54000], // 전북 군산시 섬 지역
  [56347, 56349], // 전북 부안군 섬 지역
  [57068, 57069], // 전남 영광군 섬 지역
  [58760, 58762], // 전남 목포시 섬 지역
  [58800, 58810], // 전남 신안군
  [58816, 58818], // 전남 신안군
  [58826, 58826], // 전남 신안군
  [58828, 58866], // 전남 신안군
  [58953, 58958], // 전남 진도군 섬 지역
  [59102, 59103], // 전남 완도군 섬 지역
  [59106, 59106], // 전남 완도군 섬 지역
  [59127, 59127], // 전남 완도군 섬 지역
  [59129, 59129], // 전남 완도군 섬 지역
  [59137, 59166], // 전남 완도군 섬 지역
  [59650, 59650], // 전남 여수시 섬 지역
  [59766, 59766], // 전남 여수시 섬 지역
  [59781, 59790], // 전남 여수시 섬 지역
];

const POSTAL_CODE_PATTERN = /^[0-9]{5}$/;
/** 배송지 입력의 우편번호 — 앞뒤 공백만 지우고 5자리 숫자만 받는다(하이픈·안쪽 공백·4/6자리·빈 값 거부). */
export const postalCodeSchema = z
  .string()
  .trim()
  .regex(POSTAL_CODE_PATTERN, { message: '우편번호는 숫자 5자리로 입력해 주세요.' });

const inRange = (value: number, [start, end]: readonly [number, number]): boolean =>
  value >= start && value <= end;

/** 읽기용 — 이행한 옛 행처럼 5자리 숫자가 아니면 null(지역을 알 수 없음). 앞뒤 공백도 형식 위반으로 본다. */
export const findShippingRegion = (postalCode: string): ShippingRegion | null => {
  if (!POSTAL_CODE_PATTERN.test(postalCode)) return null;
  const value = Number(postalCode);
  if (inRange(value, JEJU_POSTAL_CODE_RANGE)) return 'JEJU';
  if (REMOTE_POSTAL_CODE_RANGES.some((range) => inRange(value, range))) return 'REMOTE';
  return 'STANDARD';
};

/** 쓰기·계산용 — 5자리 숫자가 아니면 STANDARD 로 떨어뜨리지 않고 던진다(입력은 `postalCodeSchema` 가 먼저 거른다). */
export const classifyShippingRegion = (postalCode: string): ShippingRegion => {
  const region = findShippingRegion(postalCode);
  if (region === null) throw new RangeError('우편번호는 숫자 5자리여야 합니다.');
  return region;
};

const shippingFeeAmountSchema = z.number().int().min(0).max(SHIPPING_FEE_MAX);
const shippingFeeSettingsShape = {
  baseFee: shippingFeeAmountSchema,
  jejuExtraFee: shippingFeeAmountSchema,
  remoteExtraFee: shippingFeeAmountSchema,
};
export const SHIPPING_FEE_TOTAL_MAX_MESSAGE = `기본 배송비와 추가 요금의 합은 ${SHIPPING_FEE_MAX.toLocaleString('ko-KR')}원 이하여야 해요.`;
/**
 * 배송비 기본값(원, 부가세 포함) — 관리자 '배송비 설정'과 견적 요청 폼 응답이 같은 모양을 쓴다.
 * 기본 + 가장 큰 지역 추가가 SHIPPING_FEE_MAX 를 넘지 않아야 한다 — 추천 배송비(`calculateShippingFee`)·배송비 줄 상한과 같은 기준.
 * 합계 검사는 오류 path 가 빈 배열(폼 전체)이다.
 */
export const shippingFeeSettingsSchema = z
  .strictObject(shippingFeeSettingsShape)
  .refine(
    (settings) =>
      settings.baseFee + Math.max(settings.jejuExtraFee, settings.remoteExtraFee) <=
      SHIPPING_FEE_MAX,
    { message: SHIPPING_FEE_TOTAL_MAX_MESSAGE },
  );
export type ShippingFeeSettings = z.infer<typeof shippingFeeSettingsSchema>;
/** spec D3 기본값 — DB 첫 행(migration)과 같고, 설정 행이 없을 때 api 가 쓴다. */
export const DEFAULT_SHIPPING_FEE_SETTINGS: ShippingFeeSettings = {
  baseFee: 4000,
  jejuExtraFee: 3000,
  remoteExtraFee: 5000,
};

/** 배송지 1곳의 배송비 = 기본 + 지역 추가(spec D1·D2). */
export const calculateShippingFee = (
  region: ShippingRegion,
  settings: ShippingFeeSettings,
): number =>
  settings.baseFee +
  (region === 'JEJU' ? settings.jejuExtraFee : region === 'REMOTE' ? settings.remoteExtraFee : 0);

/** 회원 견적 요청의 배송지 한 곳 — 기존 배송지 5칸(우편번호는 5자리 숫자로 좁힘) + 이 배송지로 받을 수량. */
export const memberDestinationInputSchema = shippingAddressSchema.extend({
  postalCode: postalCodeSchema,
  quantity: quoteQuantitySchema,
  /** 사이즈 상품이면 사이즈별 수량(합 = quantity, 요청 입력 superRefine 이 본다). 사이즈 없는 상품은 생략. */
  sizes: destinationSizeListSchema.optional(),
});
export type MemberDestinationInput = z.infer<typeof memberDestinationInputSchema>;

/** 비회원 견적 요청의 배송지 한 곳(spec D8) — 받는 분·연락처·상세주소는 주문할 때 받는다. */
export const guestDestinationInputSchema = z.strictObject({
  postalCode: postalCodeSchema,
  address1: z.string().trim().min(1).max(SHIPPING_ADDRESS_MAX_LENGTHS.address1),
  quantity: quoteQuantitySchema,
  sizes: destinationSizeListSchema.optional(),
});
export type GuestDestinationInput = z.infer<typeof guestDestinationInputSchema>;

export const DESTINATION_LIMIT_MESSAGE = '배송지는 10곳까지 넣을 수 있어요';
/** 배송지 목록 1~10곳. 수량 합계·최소 주문 수량은 서버 유스케이스가 본다(`INVALID_QUANTITY`·`INVALID_DESTINATIONS`). */
export const destinationListSchema = <T extends z.ZodType>(item: T) =>
  z
    .array(item)
    .min(1, { message: '배송지를 한 곳 이상 넣어 주세요.' })
    .max(MAX_DESTINATIONS, { message: DESTINATION_LIMIT_MESSAGE });

export const QUOTE_LINE_KINDS = ['PRODUCT', 'SHIPPING'] as const;
export type QuoteLineKind = (typeof QUOTE_LINE_KINDS)[number];
export const quoteLineKindSchema = z.enum(QUOTE_LINE_KINDS);

export interface QuoteAmountBreakdown {
  readonly productAmount: number;
  readonly shippingAmount: number;
  readonly totalAmount: number;
  readonly supplyAmount: number;
  readonly vatAmount: number;
}
/**
 * 표시용 금액 나누기(spec §7) — 상품 = PRODUCT 줄 합, 배송비 = SHIPPING 줄 합, 합계·공급가·부가세는 `splitVatInclusiveTotal`.
 * 저장 금액(`quote_versions.total_amount`)은 지금처럼 줄 합계이고 이 함수는 화면·메일 표시만 만든다.
 */
export const splitQuoteAmounts = (
  lines: readonly { readonly kind: QuoteLineKind; readonly amount: number }[],
): QuoteAmountBreakdown => {
  let productAmount = 0;
  let shippingAmount = 0;
  for (const line of lines) {
    if (line.kind === 'SHIPPING') shippingAmount += line.amount;
    else productAmount += line.amount;
  }
  const totalAmount = productAmount + shippingAmount;
  return { productAmount, shippingAmount, totalAmount, ...splitVatInclusiveTotal(totalAmount) };
};

/** `quote_version_lines.name` CHECK(1~100자)·`quoteDraftLineSchema.name max(100)` 과 같다. */
export const SHIPPING_LINE_NAME_MAX_LENGTH = 100;
/**
 * 배송비 줄 이름 `배송비 · 배송지 {sortOrder + 1} ({지역})` — 요청 초안·관리자 '기본값으로' 가 같은 이름을 쓴다.
 * 받는 분·주소를 넣지 않는다: 줄 이름은 비회원 링크 화면(주소를 보여 주지 않는 곳)에도 실린다.
 */
export const shippingLineName = (input: {
  readonly sortOrder: number;
  readonly region: ShippingRegion;
}): string => `배송비 · 배송지 ${input.sortOrder + 1} (${SHIPPING_REGION_LABELS[input.region]})`;

/**
 * 견적 배송지 출력(고객·관리자 상세, 주문하기 화면의 원본). 비회원 출처는 받는 분·연락처·상세주소가 null(spec D8).
 * `region` 은 `findShippingRegion` 결과 — 이행한 옛 행의 우편번호가 5자리 숫자가 아니면 null 이다.
 */
export const quoteDestinationSchema = z.strictObject({
  id: uuidSchema,
  sortOrder: quoteIntegerSchema.min(0),
  quantity: quoteQuantitySchema,
  /** 사이즈 없는 상품이면 빈 배열. */
  sizes: z.array(destinationSizeSchema),
  region: shippingRegionSchema.nullable(),
  recipientName: z.string().nullable(),
  recipientPhone: z.string().nullable(),
  postalCode: z.string(),
  address1: z.string(),
  address2: z.string().nullable(),
});
export type QuoteDestination = z.infer<typeof quoteDestinationSchema>;

/**
 * 주문 배송지 출력의 기본형(배송지 사본 + 수량). 관리자·고객 출력은 Task 6 이 `orders.ts` 에서 송장 목록·변경자를 더해 넓힌다.
 */
export const orderDestinationSchema = z.strictObject({
  id: uuidSchema,
  sortOrder: quoteIntegerSchema.min(0),
  quantity: quoteQuantitySchema,
  sizes: z.array(destinationSizeSchema),
  region: shippingRegionSchema.nullable(),
  quoteDestinationId: uuidSchema.nullable(),
  shippingAddress: shippingAddressSchema,
  addressUpdatedAt: z.iso.datetime().nullable(),
});
export type OrderDestination = z.infer<typeof orderDestinationSchema>;

/** 관리자 '배송비 설정' 저장 입력(spec §6-2·D9) — 세 금액만 받는다. 변경자·시각은 서버가 정한다. */
export const updateShippingFeeSettingsInputSchema = shippingFeeSettingsSchema;
export type UpdateShippingFeeSettingsInput = ShippingFeeSettings;

/**
 * 관리자 '배송비 설정' 조회·저장 결과 — 변경자 이름은 화면 표시용, 연락처는 싣지 않는다.
 * 출력은 합계 검사를 하지 않는다 — 저장된 값이 어떻든 관리자가 화면에서 보고 고칠 수 있어야 한다.
 */
export const adminShippingFeeSettingsSchema = z.strictObject({
  ...shippingFeeSettingsShape,
  updatedAt: z.iso.datetime().nullable(), // 행이 없으면 null
  updatedBy: z.strictObject({ id: uuidSchema, name: z.string() }).nullable(), // migration 첫 행·탈퇴 처리된 관리자 FK SET NULL 이면 null
});
export type AdminShippingFeeSettings = z.infer<typeof adminShippingFeeSettingsSchema>;
